/**
 * Turns a thrown value from a Supabase/PostgREST call into something a user can
 * act on and a developer can search for.
 *
 * Generic catch blocks that toast a fixed string ("Failed to save template")
 * discard the only useful information there was. RLS rejections in particular
 * are indistinguishable from validation errors and network errors once the
 * message is thrown away, which turns a one-line diagnosis into a guessing game.
 */

/** Postgres error codes worth explaining rather than echoing verbatim. */
const CODE_EXPLANATIONS: Record<string, string> = {
  // insufficient_privilege — an RLS policy rejected the row.
  "42501":
    "you do not have permission to do this, or the record belongs to another tenant",
  // unique_violation
  "23505": "a record with these details already exists",
  // foreign_key_violation
  "23503": "a linked record is missing or was deleted",
  // not_null_violation
  "23502": "a required field is missing",
  // undefined_table
  "42P01": "a required table is missing",
};

interface PostgrestLikeError {
  message?: unknown;
  code?: unknown;
  details?: unknown;
  hint?: unknown;
}

function asRecord(value: unknown): PostgrestLikeError | null {
  return typeof value === "object" && value !== null
    ? (value as PostgrestLikeError)
    : null;
}

function asText(value: unknown): string {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : "";
}

/**
 * Builds a short human-readable description of a Supabase error.
 *
 * Returns the explanation for a known Postgres code when there is one,
 * otherwise the error's own message, otherwise a stable fallback. The code is
 * appended when present so it stays greppable in support conversations.
 */
export function describeSupabaseError(error: unknown): string {
  const record = asRecord(error);
  const code = asText(record?.code);
  const message = asText(record?.message) || asText(error);

  const explanation = code ? CODE_EXPLANATIONS[code] : undefined;
  const body = explanation || message || "unexpected error";

  return code ? `${body} (${code})` : body;
}
